import { useEffect, useMemo, useRef, useState } from "react";
import {
  BadgeCheck,
  CalendarDays,
  CirclePlus,
  FileText,
  Minus,
  Search,
  Truck,
  X,
} from "lucide-react";
import { toast } from "sonner";
import api, { getError } from "@/api";
import { PdfFileActions } from "@/components/PdfFileActions";
import { ProductCombobox } from "@/components/ProductCombobox";

const emptyDraftLine = { product_id: "", quantity: "", negotiated_sale_price: "" };

const roleLabels = {
  magasinier_principal: "Magasinier",
  coordinateur_zone: "Coordinateur de zone",
  livreur: "Livreur",
};

export default function DeliveryPage() {
  const [context, setContext] = useState(null);
  const [delivery, setDelivery] = useState({
    warehouse_id: "",
    recipient_user_id: "",
    notes: "",
    date_mode: "auto",
    movement_date: "",
    lines: [],
  });
  const [draftLine, setDraftLine] = useState(emptyDraftLine);
  const [warehouseSearch, setWarehouseSearch] = useState("");
  const [warehouseType, setWarehouseType] = useState("");
  const [zoneFilter, setZoneFilter] = useState("");
  const [subzoneFilter, setSubzoneFilter] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [createdDelivery, setCreatedDelivery] = useState(null);
  const [consolidation, setConsolidation] = useState(null);
  const [productSummary, setProductSummary] = useState(null);
  const [summaryPeriod, setSummaryPeriod] = useState("this_month");
  const [summaryDateFrom, setSummaryDateFrom] = useState("");
  const [summaryDateTo, setSummaryDateTo] = useState("");
  const [stockByProduct, setStockByProduct] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const quantityInputRef = useRef(null);
  const productInputRef = useRef(null);

  const loadContext = async () => {
    setLoading(true);
    try {
      const response = await api.get("/deliveries/context");
      setContext(response.data);
      setDelivery((current) => ({
        ...current,
        warehouse_id: current.warehouse_id || response.data.default_warehouse_id || "",
        recipient_user_id: (
          current.recipient_user_id
          || response.data.default_recipient_user_id
          || ""
        ),
      }));
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setLoading(false);
    }
  };

  const refreshWarehouseStock = async (warehouseId = delivery.warehouse_id) => {
    if (!warehouseId) {
      setStockByProduct({});
      return {};
    }

    const response = await api.get("/inventory", { params: { warehouse_id: warehouseId } });
    const stock = response.data.reduce((totals, row) => ({
      ...totals,
      [row.product_id]: Number(totals[row.product_id] || 0) + Number(row.quantity || 0),
    }), {});
    setStockByProduct(stock);
    return stock;
  };

  useEffect(() => {
    loadContext();
  }, []);

  useEffect(() => {
    if (!delivery.warehouse_id) {
      setStockByProduct({});
      return;
    }

    refreshWarehouseStock(delivery.warehouse_id)
      .catch((error) => toast.error(getError(error)));
  }, [delivery.warehouse_id]);

  const warehouses = context?.warehouses || [];
  const products = context?.products || [];
  const recipients = context?.recipients || [];
  const isLocalOperator = Boolean(context?.is_local_operator);
  const isConakryCoordinator = Boolean(context?.is_conakry_coordinator);
  const isDriverDeliveryOperator = Boolean(context?.is_driver_delivery_operator);
  const canEditDeliveryDate = Boolean(context?.can_edit_delivery_date);
  const manualDate = delivery.date_mode === "manual";
  const selectedWarehouse = warehouses.find(
    (warehouse) => warehouse.warehouse_id === delivery.warehouse_id,
  );
  const assignedRecipient = selectedWarehouse?.responsible_user_id ? (
    recipients.find((recipient) => recipient.user_id === selectedWarehouse.responsible_user_id)
    || {
      user_id: selectedWarehouse.responsible_user_id,
      name: selectedWarehouse.responsible_name,
      role: selectedWarehouse.responsible_role,
    }
  ) : null;
  const selectedRecipient = isDriverDeliveryOperator
    ? recipients.find((recipient) => recipient.user_id === delivery.recipient_user_id) || null
    : assignedRecipient?.user_id === delivery.recipient_user_id
      ? assignedRecipient
      : null;

  useEffect(() => {
    if (isDriverDeliveryOperator) {
      return;
    }
    const recipientUserId = selectedWarehouse?.responsible_user_id || "";
    setDelivery((current) => current.recipient_user_id === recipientUserId ? current : {
      ...current,
      recipient_user_id: recipientUserId,
    });
  }, [isDriverDeliveryOperator, selectedWarehouse?.responsible_user_id]);
  const zones = [...new Set(warehouses.map((warehouse) => warehouse.zone_id).filter(Boolean))].sort();
  const warehouseTypes = [...new Set(
    warehouses.map((warehouse) => warehouse.warehouse_type).filter(Boolean),
  )].sort();
  const subzones = [...new Set(
    warehouses
      .filter((warehouse) => !zoneFilter || warehouse.zone_id === zoneFilter)
      .map((warehouse) => warehouse.subzone)
      .filter(Boolean),
  )].sort();
  const filteredWarehouses = useMemo(() => {
    const query = warehouseSearch.trim().toLocaleLowerCase("fr-FR");
    return warehouses.filter((warehouse) => {
      const haystack = [
        warehouse.name,
        warehouse.warehouse_type,
        warehouse.zone_id,
        warehouse.subzone,
      ].filter(Boolean).join(" ").toLocaleLowerCase("fr-FR");
      return haystack.includes(query)
        && (!warehouseType || warehouse.warehouse_type === warehouseType)
        && (!zoneFilter || warehouse.zone_id === zoneFilter)
        && (!subzoneFilter || warehouse.subzone === subzoneFilter);
    });
  }, [subzoneFilter, warehouseSearch, warehouseType, warehouses, zoneFilter]);
  const productsById = useMemo(
    () => Object.fromEntries(products.map((product) => [product.product_id, product])),
    [products],
  );
  const deliveryTotals = useMemo(() => delivery.lines.reduce((totals, line) => {
    const product = productsById[line.product_id] || {};
    const quantity = Number(line.quantity || 0);
    const cataloguePrice = Number(product.sale_price || 0);
    const negotiatedPrice = Number(line.negotiated_sale_price || cataloguePrice);
    const rate = cataloguePrice <= 200000
      ? Number(selectedRecipient?.commission?.low_price_unit || 0)
      : Number(selectedRecipient?.commission?.high_price_unit || 0);
    return {
      gross: totals.gross + cataloguePrice * quantity,
      discount: totals.discount + Math.max(0, cataloguePrice - negotiatedPrice) * quantity,
      commission: totals.commission + rate * quantity,
      units: totals.units + quantity,
    };
  }, { gross: 0, discount: 0, commission: 0, units: 0 }), [
    delivery.lines,
    productsById,
    selectedRecipient,
  ]);
  const canPrepare = Boolean(
    delivery.warehouse_id
      && delivery.recipient_user_id
      && delivery.lines.length
      && (!manualDate || delivery.movement_date),
  );

  const addLine = () => {
    const quantity = Number(draftLine.quantity);
    const available = Number(stockByProduct[draftLine.product_id] || 0);

    if (!draftLine.product_id || quantity <= 0) {
      toast.error("Choisissez un produit et une quantité positive.");
      return;
    }
    if (quantity > available) {
      toast.error("La quantité demandée dépasse le stock du dépôt sélectionné.");
      return;
    }

    const existing = delivery.lines.find((line) => line.product_id === draftLine.product_id);
    const totalQuantity = Number(existing?.quantity || 0) + quantity;
    if (totalQuantity > available) {
      toast.error("Le total de cette ligne dépasse le stock disponible.");
      return;
    }

    const lines = existing
      ? delivery.lines.map((line) => line.product_id === draftLine.product_id ? {
        ...line,
        quantity: totalQuantity,
      } : line)
      : [
        ...delivery.lines,
        {
          line_id: `${draftLine.product_id}-${Date.now()}-${delivery.lines.length}`,
          product_id: draftLine.product_id,
          quantity,
          negotiated_sale_price: Number(draftLine.negotiated_sale_price || 0),
        },
      ];
    setDelivery((current) => ({ ...current, lines }));
    setDraftLine(emptyDraftLine);
    requestAnimationFrame(() => productInputRef.current?.focus());
  };

  const selectDeliveryProduct = (product) => {
    setDraftLine({
      product_id: product?.product_id || "",
      quantity: "",
      negotiated_sale_price: product ? String(product.sale_price || 0) : "",
    });
    if (product) {
      requestAnimationFrame(() => quantityInputRef.current?.focus());
    }
  };

  const removeLine = (lineId) => {
    setDelivery((current) => ({
      ...current,
      lines: current.lines.filter((line, index) => (
        (line.line_id || `${line.product_id}-${index}`) !== lineId
      )),
    }));
    setReviewing(false);
  };

  const addLineOnEnter = (event) => {
    if (event.key === "Enter" && !event.nativeEvent.isComposing) {
      event.preventDefault();
      addLine();
    }
  };

  const submit = async () => {
    if (!canPrepare) {
      toast.error("Complétez le dépôt, le responsable, la date et au moins un produit.");
      return;
    }

    setSaving(true);
    try {
      const response = await api.post("/deliveries", {
        warehouse_id: delivery.warehouse_id,
        recipient_user_id: delivery.recipient_user_id,
        notes: delivery.notes,
        movement_date: manualDate ? delivery.movement_date : null,
        lines: delivery.lines.map(({ product_id, quantity, negotiated_sale_price }) => ({
          product_id,
          quantity,
          negotiated_sale_price,
        })),
      });
      setCreatedDelivery(response.data);
      await refreshWarehouseStock();
      setDelivery((current) => ({
        ...current,
        date_mode: "auto",
        lines: [],
        movement_date: "",
        notes: "",
      }));
      setReviewing(false);
      toast.success("Livraison validée et stock mis à jour.");
    } catch (error) {
      await refreshWarehouseStock().catch(() => {});
      toast.error(getError(error));
    } finally {
      setSaving(false);
    }
  };

  const prepareDelivery = async () => {
    try {
      const latestStock = await refreshWarehouseStock();
      const unavailableLine = delivery.lines.find((line) => (
        Number(line.quantity) > Number(latestStock[line.product_id] || 0)
      ));
      if (unavailableLine) {
        toast.error(
          `Le stock a changé : ${productsById[unavailableLine.product_id]?.name || "ce produit"}`
          + " doit être ajusté.",
        );
        return;
      }
      setReviewing(true);
    } catch (error) {
      toast.error(getError(error));
    }
  };

  const getPdfBlob = async () => {
    const response = await api.get(`/documents/${createdDelivery.document.document_id}/pdf`, {
      responseType: "blob",
    });
    return response.data;
  };

  const createConsolidation = async () => {
    try {
      const reportDate = createdDelivery?.delivery?.movement_date
        ? new Date(createdDelivery.delivery.movement_date).toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10);
      const response = await api.post("/deliveries/daily-consolidation", null, {
        params: { report_date: reportDate },
      });
      setConsolidation(response.data);
      toast.success("Rapport consolidé journalier créé.");
    } catch (error) {
      toast.error(getError(error));
    }
  };

  const getConsolidationPdfBlob = async () => {
    const response = await api.get(`/documents/${consolidation.document.document_id}/pdf`, {
      responseType: "blob",
    });
    return response.data;
  };

  const loadProductSummary = async () => {
    try {
      const response = await api.get("/deliveries/product-summary", {
        params: {
          period: summaryPeriod,
          date_from: summaryDateFrom,
          date_to: summaryDateTo,
        },
      });
      setProductSummary(response.data);
    } catch (error) {
      toast.error(getError(error));
    }
  };

  return (
    <div className="page-enter delivery-page" data-testid="delivery-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">SORTIE CONTRÔLÉE</p>
          <h1 data-testid="delivery-title">Ajouter une livraison</h1>
          <p data-testid="delivery-description">
            Préparez plusieurs produits, contrôlez les quantités puis validez le bon unique.
          </p>
        </div>
        <Truck aria-hidden="true" size={26} />
      </div>

      {loading && <p className="loading-line" data-testid="delivery-loading">Chargement…</p>}
      {!loading && context && (
        <>
          <section className="delivery-selection-band" data-testid="delivery-selection-band">
            <div className="delivery-selection-heading">
              <div>
                <p className="eyebrow">PÉRIMÈTRE DE LIVRAISON</p>
                <h2>{isLocalOperator ? "Votre dépôt autorisé" : "Dépôt et responsable"}</h2>
              </div>
              <span data-testid="delivery-operator-mode">
                {isLocalOperator ? "Périmètre personnel" : "Périmètre administré"}
              </span>
            </div>
            {!isLocalOperator && (
              <div className="delivery-filter-grid" data-testid="delivery-warehouse-filters">
                <label className="filter-search">
                  <Search aria-hidden="true" size={17} />
                  <input
                    data-testid="delivery-warehouse-search-input"
                    onChange={(event) => setWarehouseSearch(event.target.value)}
                    placeholder="Rechercher un dépôt"
                    value={warehouseSearch}
                  />
                </label>
                <select
                  data-testid="delivery-warehouse-type-filter"
                  onChange={(event) => setWarehouseType(event.target.value)}
                  value={warehouseType}
                >
                  <option value="">Tous les types</option>
                  {warehouseTypes.map((type) => <option key={type} value={type}>{type}</option>)}
                </select>
                <select
                  data-testid="delivery-zone-filter"
                  onChange={(event) => {
                    setZoneFilter(event.target.value);
                    setSubzoneFilter("");
                  }}
                  value={zoneFilter}
                >
                  <option value="">Toutes les zones</option>
                  {zones.map((zone) => <option key={zone} value={zone}>{zone}</option>)}
                </select>
                <select
                  data-testid="delivery-subzone-filter"
                  disabled={!zoneFilter}
                  onChange={(event) => setSubzoneFilter(event.target.value)}
                  value={subzoneFilter}
                >
                  <option value="">Toutes les sous-zones</option>
                  {subzones.map((subzone) => <option key={subzone} value={subzone}>{subzone}</option>)}
                </select>
              </div>
            )}
            <div className="delivery-selection-grid">
              <label>
                <span>Dépôt concerné</span>
                <select
                  data-testid="delivery-warehouse-select"
                  onChange={(event) => setDelivery((current) => ({
                    ...current,
                    warehouse_id: event.target.value,
                    recipient_user_id: warehouses.find(
                      (warehouse) => warehouse.warehouse_id === event.target.value,
                    )?.responsible_user_id || "",
                    lines: [],
                  }))}
                  value={delivery.warehouse_id}
                >
                  <option value="">Choisir un dépôt</option>
                  {filteredWarehouses.map((warehouse) => (
                    <option key={warehouse.warehouse_id} value={warehouse.warehouse_id}>
                      {warehouse.name} · {warehouse.subzone}
                    </option>
                  ))}
                </select>
              </label>
              <div className="delivery-local-recipient" data-testid="delivery-assigned-recipient">
                <label>
                  <span>{isConakryCoordinator ? "Livreur de votre zone" : isDriverDeliveryOperator ? "Livreur" : "Responsable affecté"}</span>
                  <select
                    data-testid="delivery-recipient-select"
                    disabled={isDriverDeliveryOperator ? !recipients.length : !assignedRecipient}
                    onChange={(event) => setDelivery((current) => ({
                      ...current,
                      recipient_user_id: event.target.value,
                    }))}
                    value={delivery.recipient_user_id}
                  >
                    <option value="">
                      {isDriverDeliveryOperator
                        ? "Choisir un livreur"
                        : assignedRecipient ? "Choisir le responsable affecté" : "Aucun responsable affecté"}
                    </option>
                    {isDriverDeliveryOperator ? recipients.map((recipient) => (
                      <option key={recipient.user_id} value={recipient.user_id}>
                        {recipient.name} · Livreur
                      </option>
                    )) : assignedRecipient && (
                      <option value={assignedRecipient.user_id}>
                        {assignedRecipient.name} · {roleLabels[assignedRecipient.role]}
                      </option>
                    )}
                  </select>
                </label>
                {!isDriverDeliveryOperator && !assignedRecipient && (
                  <small data-testid="delivery-missing-responsible-message">
                    Affectez un responsable au dépôt avant de préparer une livraison.
                  </small>
                )}
              </div>
            </div>
            <div className="delivery-date-control" data-testid="delivery-date-control">
              <span><CalendarDays aria-hidden="true" size={15} /> Date de la livraison</span>
              <div className="movement-date-mode-buttons">
                <button
                  className={!manualDate ? "active" : ""}
                  data-testid="delivery-date-auto-button"
                  onClick={() => setDelivery((current) => ({
                    ...current,
                    date_mode: "auto",
                    movement_date: "",
                  }))}
                  type="button"
                >
                  Automatique
                </button>
                {canEditDeliveryDate && (
                  <button
                    className={manualDate ? "active" : ""}
                    data-testid="delivery-date-manual-button"
                    onClick={() => setDelivery((current) => ({
                      ...current,
                      date_mode: "manual",
                      movement_date: current.movement_date
                        || new Date().toISOString().slice(0, 10),
                    }))}
                    type="button"
                  >
                    Saisie manuelle
                  </button>
                )}
              </div>
              {manualDate ? (
                <input
                  data-testid="delivery-manual-date-input"
                  lang="fr-FR"
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(event) => setDelivery((current) => ({
                    ...current,
                    movement_date: event.target.value,
                  }))}
                  required
                  type="date"
                  value={delivery.movement_date}
                />
              ) : (
                <small data-testid="delivery-auto-date-help">
                  {canEditDeliveryDate
                    ? "La date et l’heure actuelles seront appliquées automatiquement."
                    : "La date est automatique. Sa modification est réservée aux administrateurs."}
                </small>
              )}
            </div>
          </section>

          <section className="data-panel delivery-lines-panel" data-testid="delivery-lines-panel">
            <div className="panel-heading">
              <div>
                <p className="eyebrow">PRODUITS À LIVRER</p>
                <h2>Ajouter une ou plusieurs lignes</h2>
              </div>
              <BadgeCheck aria-hidden="true" size={21} />
            </div>
            <div className="line-adder delivery-line-adder delivery-discount-adder" data-testid="delivery-line-adder">
              <div>
                <ProductCombobox
                  inputRef={productInputRef}
                  onSelect={selectDeliveryProduct}
                  placeholder="Rechercher un produit par nom ou tags"
                  products={products}
                  testIdPrefix="delivery-product"
                  value={draftLine.product_id}
                />
              </div>
              <input
                data-testid="delivery-quantity-input"
                min="0.01"
                onChange={(event) => setDraftLine((current) => ({
                  ...current,
                  quantity: event.target.value,
                }))}
                onKeyDown={addLineOnEnter}
                placeholder="Qté"
                ref={quantityInputRef}
                step="0.01"
                type="number"
                value={draftLine.quantity}
              />
              {isDriverDeliveryOperator && (
                <input
                  data-testid="delivery-negotiated-price-input"
                  min="0"
                  onChange={(event) => setDraftLine((current) => ({
                    ...current,
                    negotiated_sale_price: event.target.value,
                  }))}
                  placeholder="Prix réduit"
                  step="1"
                  type="number"
                  value={draftLine.negotiated_sale_price}
                />
              )}
              <button
                aria-label="Ajouter le produit à la livraison"
                data-testid="add-delivery-line-button"
                onClick={addLine}
                title="Ajouter le produit"
                type="button"
              >
                <CirclePlus size={18} />
              </button>
            </div>
            {draftLine.product_id && (
              <p className="delivery-availability" data-testid="delivery-selected-product-availability">
                Stock disponible dans {selectedWarehouse?.name || "le dépôt sélectionné"} : {" "}
                <strong>{stockByProduct[draftLine.product_id] || 0} unité(s)</strong>
              </p>
            )}
            <div className="delivery-line-list" data-testid="delivery-line-list">
              {delivery.lines.map((line, index) => {
                const lineId = line.line_id || `${line.product_id}-${index}`;
                const product = productsById[line.product_id];
                return (
                  <div data-testid={`delivery-line-${lineId}`} key={lineId}>
                    <span>
                      <strong>{product?.name || "Produit"}</strong>
                      <small>Stock avant sortie : {stockByProduct[line.product_id] || 0}</small>
                    </span>
                    <b>
                      {line.quantity} unité(s) · {Number(line.negotiated_sale_price).toLocaleString("fr-FR")} GNF
                    </b>
                    <button
                      aria-label="Retirer ce produit de la livraison"
                      data-testid={`remove-delivery-line-${lineId}`}
                      onClick={() => removeLine(lineId)}
                      title="Retirer"
                      type="button"
                    >
                      <Minus size={16} />
                    </button>
                  </div>
                );
              })}
              {!delivery.lines.length && (
                <p className="empty-inline" data-testid="empty-delivery-lines-message">
                  Ajoutez les produits à remettre avant de préparer la livraison.
                </p>
              )}
            </div>
            {isDriverDeliveryOperator && (
              <div className="delivery-financial-summary" data-testid="delivery-financial-summary">
                <span>Produits<strong>{delivery.lines.length} ligne(s) · {deliveryTotals.units} unité(s)</strong></span>
                <span>Valeur totale<strong>{deliveryTotals.gross.toLocaleString("fr-FR")} GNF</strong></span>
                <span>Réduction<strong>{deliveryTotals.discount.toLocaleString("fr-FR")} GNF</strong></span>
                <span>Commission livreur<strong>{deliveryTotals.commission.toLocaleString("fr-FR")} GNF</strong></span>
                <span>Net à verser<strong>{(deliveryTotals.gross - deliveryTotals.discount - deliveryTotals.commission).toLocaleString("fr-FR")} GNF</strong></span>
              </div>
            )}
            <label className="delivery-notes-field">
              <span>Observation</span>
              <textarea
                data-testid="delivery-notes-input"
                onChange={(event) => setDelivery((current) => ({
                  ...current,
                  notes: event.target.value,
                }))}
                placeholder="Précision facultative pour le bon de livraison"
                value={delivery.notes}
              />
            </label>
            <button
              className="primary-action"
              data-testid="prepare-delivery-button"
              disabled={!canPrepare}
              onClick={prepareDelivery}
              type="button"
            >
              <FileText size={17} /> Contrôle et validation
            </button>
          </section>

          {reviewing && (
            <div
              aria-labelledby="delivery-review-title"
              aria-modal="true"
              className="delivery-review-modal"
              data-testid="delivery-review-modal"
              role="dialog"
            >
              <section className="delivery-review-panel" data-testid="delivery-review-panel">
                <header className="delivery-review-header">
                  <div>
                    <p className="eyebrow">CONTRÔLE AVANT VALIDATION</p>
                    <h2 id="delivery-review-title">Récapitulatif de livraison</h2>
                  </div>
                  <button
                    aria-label="Fermer le récapitulatif"
                    className="delivery-review-close-button"
                    data-testid="close-delivery-review-button"
                    onClick={() => setReviewing(false)}
                    title="Fermer"
                    type="button"
                  >
                    <X size={18} />
                  </button>
                </header>
                <div className="delivery-review-scroll" data-testid="delivery-review-scroll">
                  <dl>
                    <div><dt>Dépôt</dt><dd>{selectedWarehouse?.name}</dd></div>
                    <div><dt>Responsable</dt><dd>{selectedRecipient?.name}</dd></div>
                    <div>
                      <dt>Date</dt>
                      <dd>
                        {new Date(
                          `${manualDate ? delivery.movement_date : new Date().toISOString().slice(0, 10)}T12:00:00`,
                        ).toLocaleDateString("fr-FR")}
                      </dd>
                    </div>
                    <div><dt>Produits</dt><dd>{delivery.lines.length} ligne(s)</dd></div>
                    {isDriverDeliveryOperator && (
                      <>
                        <div><dt>Valeur totale</dt><dd>{deliveryTotals.gross.toLocaleString("fr-FR")} GNF</dd></div>
                        <div><dt>Réduction accordée</dt><dd>{deliveryTotals.discount.toLocaleString("fr-FR")} GNF</dd></div>
                        <div><dt>Commission livraison</dt><dd>{deliveryTotals.commission.toLocaleString("fr-FR")} GNF</dd></div>
                        <div><dt>Net à verser</dt><dd>{(deliveryTotals.gross - deliveryTotals.discount - deliveryTotals.commission).toLocaleString("fr-FR")} GNF</dd></div>
                      </>
                    )}
                  </dl>
                  <div className="delivery-review-lines" data-testid="delivery-review-lines">
                    {delivery.lines.map((line, index) => (
                      <div key={line.line_id || `${line.product_id}-${index}`}>
                        <strong>{productsById[line.product_id]?.name || "Produit"}</strong>
                        <span>
                          {line.quantity} unité(s) sur {stockByProduct[line.product_id] || 0}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <footer className="delivery-review-actions">
                  <button
                    className="secondary-action"
                    data-testid="edit-delivery-button"
                    onClick={() => setReviewing(false)}
                    type="button"
                  >
                    Modifier
                  </button>
                  <button
                    className="primary-action"
                    data-testid="validate-delivery-button"
                    disabled={saving}
                    onClick={submit}
                    type="button"
                  >
                    <BadgeCheck size={17} /> {saving ? "Validation…" : "Valider la livraison"}
                  </button>
                </footer>
              </section>
            </div>
          )}

          {createdDelivery && (
            <section className="delivery-success-band" data-testid="delivery-success-band">
              <div>
                <p className="eyebrow">LIVRAISON VALIDÉE</p>
                <h2 data-testid="created-delivery-number">
                  {createdDelivery.delivery.delivery_number}
                </h2>
                <p>
                  La sortie est enregistrée dans l’historique du dépôt et le stock est à jour.
                </p>
              </div>
              <PdfFileActions
                fileName={`${createdDelivery.delivery.delivery_number}.pdf`}
                getPdfBlob={getPdfBlob}
                testIdPrefix="created-delivery"
              />
              {isConakryCoordinator && (
                <button
                  className="secondary-action"
                  data-testid="create-daily-delivery-consolidation-button"
                  onClick={createConsolidation}
                  type="button"
                >
                  <FileText size={17} />
                  Consolider les livraisons du jour
                </button>
              )}
            </section>
          )}
          {consolidation && (
            <section className="delivery-success-band" data-testid="delivery-consolidation-success-band">
              <div>
                <p className="eyebrow">CONSOLIDATION JOURNALIÈRE</p>
                <h2 data-testid="delivery-consolidation-number">
                  {consolidation.document.document_number}
                </h2>
                <p>{consolidation.delivery_count} livraison(s) consolidée(s).</p>
              </div>
              <PdfFileActions
                fileName={`${consolidation.document.document_number}.pdf`}
                getPdfBlob={getConsolidationPdfBlob}
                testIdPrefix="delivery-consolidation"
              />
            </section>
          )}
          {context?.is_delivery_admin && (
            <section className="data-panel" data-testid="admin-delivery-product-summary">
              <div className="panel-heading">
                <div>
                  <p className="eyebrow">ÉTAT ADMINISTRATIF</p>
                  <h2>Total livré par produit</h2>
                </div>
              </div>
              <div className="filters-bar">
                <select
                  data-testid="delivery-product-summary-period"
                  onChange={(event) => setSummaryPeriod(event.target.value)}
                  value={summaryPeriod}
                >
                  <option value="today">Aujourd’hui</option>
                  <option value="yesterday">Hier</option>
                  <option value="this_week">Cette semaine</option>
                  <option value="last_7_days">7 derniers jours</option>
                  <option value="this_month">Ce mois-ci</option>
                  <option value="last_30_days">30 derniers jours</option>
                  <option value="last_90_days">90 derniers jours</option>
                  <option value="this_year">Cette année</option>
                  <option value="all">Toute la période</option>
                  <option value="custom">Dates personnalisées</option>
                </select>
                {summaryPeriod === "custom" && (
                  <>
                    <input
                      data-testid="delivery-product-summary-date-from"
                      onChange={(event) => setSummaryDateFrom(event.target.value)}
                      type="date"
                      value={summaryDateFrom}
                    />
                    <input
                      data-testid="delivery-product-summary-date-to"
                      onChange={(event) => setSummaryDateTo(event.target.value)}
                      type="date"
                      value={summaryDateTo}
                    />
                  </>
                )}
                <button
                  className="secondary-action"
                  data-testid="load-delivery-product-summary"
                  onClick={loadProductSummary}
                  type="button"
                >
                  Afficher
                </button>
              </div>
              {productSummary && (
                <div className="responsive-table">
                  <table data-testid="delivery-product-summary-table">
                    <thead><tr><th>Produit</th><th>Quantité livrée</th><th>Valeur réelle</th></tr></thead>
                    <tbody>
                      {productSummary.rows.map((row) => (
                        <tr key={row.product_id}>
                          <td>{row.product_name}</td>
                          <td>{row.quantity}</td>
                          <td>{row.value.toLocaleString("fr-FR")} GNF</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot><tr><td>Total</td><td>{productSummary.total_quantity}</td><td>{productSummary.total_value.toLocaleString("fr-FR")} GNF</td></tr></tfoot>
                  </table>
                </div>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}